import { Text, UnstyledButton, UnstyledButtonProps } from "@mantine/core";
import { modals } from "@mantine/modals";
import { hideNotification, showNotification, updateNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import { ForwardedRef, PropsWithChildren, forwardRef, useCallback, useId, useMemo } from "react";
import { useConfiguracaoFormularioIdeias } from "@/modules/configuracao-formulario-ideias";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { publishIdeia } from "../../firestore";
import { getRequiredFieldsForPublish } from "../../utils/validation";
import { QUERY_KEY } from "../ideias-table/IdeiasTable";

type TPublishIdeiaResponse = Awaited<ReturnType<typeof publishIdeia>>;

/**
 * Mutation that publishes an ideia.
 * @param ideia - The ideia to be publicada.
 * @returns The mutation to be used.
 */
function useSetPublishAtMutation(ideia: TIdeia) {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const queryClient = useQueryClient();

  const notificationId = useId();

  return useMutation<TPublishIdeiaResponse, FirestoreError>({
    mutationFn() {
      hideNotification(notificationId);
      if (!activeEmpresaId) {
        throw new Error("Empresa não selecionada");
      }
      showNotification({
        title: "Publicando ideia",
        message: "Aguarde enquanto publicamos sua ideia",
        autoClose: false,
        id: notificationId,
        loading: true
      });
      return publishIdeia(activeEmpresaId, ideia.id);
    },
    onSuccess() {
      updateNotification({
        id: notificationId,
        title: "Ideia publicada",
        color: "green",
        message: "Sua ideia foi publicada com sucesso",
        autoClose: true,
        loading: false
      });
      return queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError(error) {
      captureException(error, true);
      hideNotification(notificationId);
    }
  });
}

type PublicarIdeiaButtonProperties = PropsWithChildren<
  UnstyledButtonProps & { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; ideia: TIdeia }
>;

/**
 * A button that publishes an ideia after validation.
 * @param root0 - The properties of the button.
 * @param root0.children - The children of the button.
 * @param root0.ideia - The ideia to be publicada.
 * @param reference - The reference to the button.
 * @returns The element to be rendered.
 */
function PublicarIdeiaButton(
  { ideia, children, ...properties }: PublicarIdeiaButtonProperties,
  reference: ForwardedRef<HTMLButtonElement>
) {
  const { mutate, isPending } = useSetPublishAtMutation(ideia);
  const { data: configuracao } = useConfiguracaoFormularioIdeias();

  const requiredFields = useMemo(() => getRequiredFieldsForPublish(configuracao), [configuracao]);

  /**
   * Handles the click event on the button.
   * Validates required fields before opening confirmation modal.
   * @param event - The event that triggered the click.
   */
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (properties.onClick) {
        properties.onClick(event);
      }

      // Validate required fields before publishing
      const missingFields = requiredFields.filter((field) => {
        const value = ideia[field];
        return !value || (typeof value === "string" && value.trim() === "");
      });

      if (missingFields.length > 0) {
        showNotification({
          title: "Campos obrigatórios faltando",
          message: `Preencha os seguintes campos antes de publicar: ${missingFields.join(", ")}`,
          color: "red",
          autoClose: 5000
        });
        return;
      }

      modals.openConfirmModal({
        title: "Deseja publicar sua ideia?",
        children: (
          <Text size="sm">
            Ao publicar sua ideia, ela será visível para todos os usuários e não poderá ser editada ou removida.
          </Text>
        ),
        labels: { confirm: "Publicar", cancel: "Cancelar" },
        onConfirm: () => mutate()
      });
    },
    [properties, requiredFields, ideia, mutate]
  );

  return (
    <UnstyledButton ref={reference} {...properties} disabled={isPending} onClick={handleClick}>
      {children}
    </UnstyledButton>
  );
}

const PublicarIdeiaButtonWithReference = forwardRef(PublicarIdeiaButton);

PublicarIdeiaButtonWithReference.displayName = "PublicarIdeiaButton";

export default PublicarIdeiaButtonWithReference;
// export default createPolymorphicComponent<"button", PublicarIdeiaButtonProperties>(PublicarIdeiaButtonWithReference);
